import { useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { DayOfWeek } from '@weareopenr/types';
import {
  EditIcon,
  IconButton,
  IOption,
  MenuIcon,
  PriceBandIcon,
  Plus,
  Tile,
  TileVariant,
  XIcon,
} from '@weareopenr/component-library';
import { ITimetableDay, ITimetableDaySchedule } from 'src/utils/timetables/groupSchedulesByDay';
import { toGridTemplateColumns, TimetableScheduleTrack } from 'src/utils/timetables/helpers';
import { mergeAdjacentInvalidSchedules } from 'src/utils/timetables/mergeAdjacentInvalidSchedules';
import { getDayLabel } from 'src/helpers/day/day';
import { TimetableView } from 'src/pages/Timetable/Timetable';
import styles from './TimetableDay.module.scss';

export interface TimetableDayProps {
  day: ITimetableDay;
  view: TimetableView;
  onAddSchedule: (track: TimetableScheduleTrack, day: DayOfWeek) => void;
  onManageSchedule?: (track: TimetableScheduleTrack, day: DayOfWeek, schedule: ITimetableDaySchedule) => void;
  isActionable: boolean;
  // Tiles to draw with a dashed border - those a pending tray preview would change.
  changedScheduleIds?: Set<string>;
  onDeleteSchedule?: (schedule: ITimetableDaySchedule, track: TimetableScheduleTrack) => void;
  invalidScheduleIds?: Set<string>;
}

const NO_DEFAULT_LABEL: Record<TimetableScheduleTrack, string> = {
  menu: '(!) No default menu',
  price_scheme: '(!) No default price scheme',
};

interface ITimetableLane {
  key: string;
  Icon: typeof MenuIcon;
  items: ITimetableDaySchedule[];
  variant: TileVariant;
  addLabel: string;
  track: TimetableScheduleTrack;
  hidden: boolean;
}

// The button's fade duration - kept in sync with the CSS transition durations below, since
// CSSTransition needs to know it too, to time removing the node from the DOM until it's done.
const ADD_BUTTON_TRANSITION_MS = 150;

interface TimetableDayRowProps {
  Icon: typeof MenuIcon;
  items: ITimetableDaySchedule[];
  variant: TileVariant;
  addLabel: string;
  track: TimetableScheduleTrack;
  day: DayOfWeek;
  isActionable: boolean;
  changedScheduleIds: Set<string>;
  invalidScheduleIds: Set<string>;
  onAddSchedule: (track: TimetableScheduleTrack, day: DayOfWeek) => void;
  onManageSchedule?: (track: TimetableScheduleTrack, day: DayOfWeek, schedule: ITimetableDaySchedule) => void;
  onDeleteSchedule?: (schedule: ITimetableDaySchedule, track: TimetableScheduleTrack) => void;
}

const TimetableDayRow = ({
  Icon,
  items,
  variant,
  addLabel,
  track,
  day,
  isActionable,
  changedScheduleIds,
  invalidScheduleIds,
  onAddSchedule,
  onManageSchedule,
  onDeleteSchedule,
}: TimetableDayRowProps) => {
  const addButtonRef = useRef<HTMLDivElement>(null);
  const [tileResetCount, setTileResetCount] = useState(0);

  // Remounts the row's tiles once an action hands over to the tray or prompt, so none is left
  // holding a stale open-menu or hover state underneath it.
  const resetTiles = () => setTileResetCount((count) => count + 1);

  // The default schedule is the fallback for the whole timetable, so it can't be amended or removed
  // here (that's "Edit details") and gets no menu.
  const getTileActions = (item: ITimetableDaySchedule): IOption[] | undefined => {
    if (item.isDefault || !isActionable) return undefined;

    const actions: IOption[] = [];
    if (onManageSchedule) {
      actions.push({
        id: 'manage-time-slot',
        name: 'Manage time slot',
        icon: EditIcon,
        onOptionClick: () => {
          resetTiles();
          onManageSchedule(track, day, item);
        },
      });
    }
    if (onDeleteSchedule) {
      if (actions.length > 0) actions.push({ id: 'divider', isDivider: true });
      actions.push({
        id: 'delete',
        name: 'Remove from timetable',
        icon: XIcon,
        hoverColour: 'tomato',
        onOptionClick: () => {
          resetTiles();
          onDeleteSchedule(item, track);
        },
      });
    }
    return actions.length > 0 ? actions : undefined;
  };

  const displayItems = mergeAdjacentInvalidSchedules(items, invalidScheduleIds);

  return (
    <div className={styles['timetable-day-row']}>
      <div className={styles['timetable-day-icon']}>
        <Icon size="small" />
      </div>
      <div
        className={styles['timetable-day-tiles']}
        style={{ gridTemplateColumns: toGridTemplateColumns(displayItems) }}
      >
        {displayItems.map((item) => {
          const isInvalid = invalidScheduleIds.has(item.scheduleId);
          return (
            <div key={`${item.scheduleId}-${tileResetCount}`} className={styles['timetable-day-tile']}>
              <Tile
                mainText={isInvalid ? NO_DEFAULT_LABEL[track] : item.name}
                secondaryText={`${item.startTime} - ${item.endTime}`}
                colour={isInvalid ? 'liquorice' : item.colour}
                variant={variant}
                hasAccent
                label={item.isDefault ? 'Default' : undefined}
                border={isInvalid || changedScheduleIds.has(item.scheduleId) ? 'dashed' : 'solid'}
                className={isInvalid ? styles['invalid-tile'] : undefined}
                actions={isInvalid ? undefined : getTileActions(item)}
              />
            </div>
          );
        })}
      </div>

      <CSSTransition
        in={isActionable}
        nodeRef={addButtonRef}
        timeout={ADD_BUTTON_TRANSITION_MS}
        classNames={{
          enter: styles['add-button-enter'],
          enterActive: styles['add-button-enter-active'],
          exit: styles['add-button-exit'],
          exitActive: styles['add-button-exit-active'],
        }}
        mountOnEnter
        unmountOnExit
      >
        <div ref={addButtonRef} className={styles['timetable-day-add-button']}>
          <IconButton
            icon={Plus}
            aria-label={addLabel}
            aria-hidden={!isActionable}
            tabIndex={isActionable ? 0 : -1}
            buttonStyle="outline"
            onClick={() => onAddSchedule(track, day)}
          />
        </div>
      </CSSTransition>
    </div>
  );
};

export const TimetableDay = ({
  day,
  view,
  onAddSchedule,
  onManageSchedule,
  isActionable,
  changedScheduleIds = new Set(),
  invalidScheduleIds = new Set(),
  onDeleteSchedule,
}: TimetableDayProps) => {
  const lanes: ITimetableLane[] = [
    {
      key: 'menu',
      Icon: MenuIcon,
      items: day.menus,
      variant: 'muted',
      addLabel: 'Add menu schedule',
      track: 'menu',
      hidden: view === TimetableView.PRICING_ONLY,
    },
    {
      key: 'price',
      Icon: PriceBandIcon,
      items: day.priceSchemes,
      variant: 'bright',
      addLabel: 'Add price scheme schedule',
      track: 'price_scheme',
      hidden: view === TimetableView.MENUS_ONLY,
    },
  ];

  return (
    <div className={styles['timetable-day']}>
      <div className={styles['timetable-day-label']}>{getDayLabel(day.day)}</div>
      <div className={styles['timetable-day-rows']}>
        {lanes
          .filter((lane) => !lane.hidden)
          .map(({ key, Icon, items, variant, addLabel, track }) => (
            <TimetableDayRow
              key={key}
              Icon={Icon}
              items={items}
              variant={variant}
              addLabel={addLabel}
              track={track}
              day={day.day}
              isActionable={isActionable}
              changedScheduleIds={changedScheduleIds}
              invalidScheduleIds={invalidScheduleIds}
              onAddSchedule={onAddSchedule}
              onManageSchedule={onManageSchedule}
              onDeleteSchedule={onDeleteSchedule}
            />
          ))}
      </div>
    </div>
  );
};
